import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, ScrollText } from 'lucide-react';
import { AdminLegalEntity } from './AdminLegalEntity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const card = 'bg-slate-800 border border-slate-700 rounded-xl';
const STATUS = { signed: 'text-emerald-300', needs_resign: 'text-amber-300', unsigned: 'text-red-300', exempt: 'text-cyan-300', paid_off: 'text-slate-300' };

const Stat = ({ label, value, sub, testid }) => (
  <div className={`${card} p-4`}><p className="text-xs uppercase text-slate-400">{label}</p>
    <p className="text-xl font-bold text-white mt-1" data-testid={testid}>{value}</p>{sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}</div>
);

const NotesTable = ({ notes }) => (
  <div className={`${card} overflow-x-auto`}>
    <table className="w-full text-sm" data-testid="ledger-notes-table">
      <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase"><tr>
        {['Agreement / Note', 'Borrower', 'Status', 'Limit', 'Outstanding', 'Past due', 'Mo. interest', 'Interest paid', 'Missed', 'Collateral'].map(h => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
      </tr></thead>
      <tbody className="divide-y divide-slate-700">
        {notes.map(n => (
          <tr key={n.credit_line_id} data-testid={`ledger-note-${n.credit_line_id}`} className={n.counts_as_receivable ? '' : 'opacity-60'}>
            <td className="px-3 py-2 text-white">{n.agreement_no}<p className="text-xs text-slate-500">{n.note_no} · {n.version || 'unsigned'}</p></td>
            <td className="px-3 py-2 text-slate-300">{n.client_name}<p className="text-xs text-slate-500">{n.client_email}</p></td>
            <td className={`px-3 py-2 text-xs uppercase ${STATUS[n.status]}`} data-testid={`ledger-note-status-${n.credit_line_id}`}>{n.status.replace('_', ' ')}{n.status === 'exempt' && <p className={`normal-case ${n.agreement_signed ? 'text-emerald-400' : 'text-amber-300'}`}>{n.agreement_signed ? 'signed' : 'awaiting signature'}</p>}</td>
            <td className="px-3 py-2 text-slate-300">{usd(n.limit_usd)}</td>
            <td className="px-3 py-2 text-white">{usd(n.outstanding_usd)}</td>
            <td className={`px-3 py-2 ${n.past_due_usd ? 'text-red-400' : 'text-slate-300'}`}>{usd(n.past_due_usd)}</td>
            <td className="px-3 py-2 text-slate-300">{usd(n.monthly_interest_usd)}</td>
            <td className="px-3 py-2 text-slate-300">{usd(n.interest_collected_usd)}</td>
            <td className="px-3 py-2 text-slate-300">{n.missed_payments}</td>
            <td className="px-3 py-2 text-slate-400 text-xs">{n.collateral || 'Unsecured'}</td>
          </tr>
        ))}
        {!notes.length && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">No credit notes yet</td></tr>}
      </tbody>
    </table>
  </div>
);

const MonthlyTable = ({ months }) => (
  <div className={`${card} overflow-x-auto`}>
    <table className="w-full text-sm" data-testid="ledger-monthly-table">
      <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase"><tr>{['Month', 'Payments', 'Interest received', 'Total collected', 'Missed', 'Missed amount'].map(h => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-700">
        {months.map(m => (
          <tr key={m.month} data-testid={`ledger-month-${m.month}`} className="text-slate-300">
            <td className="px-3 py-2 text-white">{m.month}</td><td className="px-3 py-2">{m.payments}</td><td className="px-3 py-2">{usd(m.interest_received_usd)}</td>
            <td className="px-3 py-2">{usd(m.collected_usd)}</td><td className={`px-3 py-2 ${m.missed ? 'text-red-400' : ''}`}>{m.missed}</td><td className="px-3 py-2">{usd(m.missed_usd)}</td>
          </tr>
        ))}
        {!months.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No billing activity yet</td></tr>}
      </tbody>
    </table>
  </div>
);

export const AdminLedger = ({ token }) => {
  const [data, setData] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/pas-ledger/admin/ledger`, { headers: { Authorization: `Bearer ${token}` } })).data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Ledger unavailable'); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const download = async (ext) => {
    try {
      const r = await axios.get(`${API}/pas-ledger/admin/ledger.${ext}`, { ...auth, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = `beth-receivables-ledger.${ext}`; a.click();
    } catch { toast.error('Download failed'); }
  };
  if (!data) return <p className="text-slate-400">Loading ledger...</p>;
  const t = data.totals;
  const ratio = t.coverage_ratio == null ? 'n/a' : `${(t.coverage_ratio * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  return (
    <div className="space-y-6" data-testid="admin-ledger">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-xl font-bold text-white">Receivables Ledger &amp; Valuation Policy</h2>
          <p className="text-xs text-slate-400">Policy {data.policy_version} · Agreement {data.agreement_version} · {new Date(data.generated_at).toLocaleString()}</p>
          <p className="text-xs text-slate-500" data-testid="ledger-treasury-wallet">Credit source: {data.treasury_wallet}</p></div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="ledger-refresh-btn" className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => download('csv')} data-testid="ledger-download-csv" className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><Download className="w-4 h-4" /> CSV</button>
          <button onClick={() => download('pdf')} data-testid="ledger-download-pdf" className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm flex items-center gap-1"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Documented receivables" value={usd(t.receivables_usd)} sub={`${t.notes_counted} signed notes · secured ${usd(t.secured_receivables_usd)}`} testid="ledger-receivables" />
        <Stat label="Subscriber BETH @ $1" value={usd(t.subscriber_beth_usd)} sub={`${Number(t.subscriber_beth).toLocaleString()} BETH (Treasury Officers)`} testid="ledger-subscriber-beth" />
        <Stat label="Coverage ratio" value={ratio} sub="Receivables ÷ subscriber BETH at $1" testid="ledger-coverage-ratio" />
        <Stat label="Monthly interest receivable" value={usd(t.monthly_interest_receivable_usd)} sub={`Collected to date ${usd(t.interest_collected_usd)}`} testid="ledger-monthly-interest" />
      </div>
      <p className="text-xs text-slate-500" data-testid="ledger-exclusions">Excluded from coverage: exempt administrator accounts ({Number(t.exempt_beth).toLocaleString()} BETH), {t.notes_unsigned} unsigned lines, standard accounts ({Number(t.standard_beth).toLocaleString()} BETH = {usd(t.standard_beth_usd)} at 1 BETH = $1). {t.notes_needing_resign} note(s) awaiting re-signature on the new agreement.</p>
      <NotesTable notes={data.notes} />
      <h3 className="text-white font-semibold">Monthly interest received &amp; missed payments</h3>
      <MonthlyTable months={data.monthly} />
      <div className={`${card} p-6`} data-testid="ledger-policy">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><ScrollText className="w-5 h-5 text-emerald-400" /> BETH Valuation Policy</h3>
        <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">{data.policy.map(p => <li key={p.title}><b className="text-white">{p.title}.</b> {p.body}</li>)}</ol>
      </div>
      <AdminLegalEntity token={token} />
    </div>
  );
};
